import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { uniqueId } from 'lodash';
import { Subject } from 'rxjs';

import { takeUntil, tap } from 'rxjs/operators';
import { FormFieldControl } from '../form-field/form-field-control';
import { FormService } from '../form.service';
import { CountryISO } from './tel-input/enums/country-iso.enum';
import { PhoneNumberFormat } from './tel-input/enums/phone-number-format.enum';
import { SearchCountryField } from './tel-input/enums/search-country-field.enum';
import { Country } from './tel-input/model/country.model';

@Component({
  selector: 'zc-input-phone',
  templateUrl: './input-phone.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FormFieldControl,
      useExisting: InputPhoneComponent,
    },
  ],
})
export class InputPhoneComponent
  implements
    ControlValueAccessor,
    FormFieldControl<any>,
    OnInit,
    OnChanges,
    OnDestroy
{
  constructor(
    @Self() public ngControl: NgControl,
    private form: FormService,
    private ref: ChangeDetectorRef
  ) {
    ngControl.valueAccessor = this;
  }

  get value() {
    return this._value;
  }

  set value(value) {
    this._value = value;
    this.onChange(value);
    this.stateChanges.next();
  }

  @Input() disabled = false;
  @Input() id = uniqueId('control-');
  @Input() placeholder = '';
  required: boolean;
  separateDialCode = true;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  selectedCountry: CountryISO = CountryISO.UnitedStates;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  readonly stateChanges = new Subject<void>();
  private _value: any;
  private readonly _destroyed = new Subject<void>();

  ngOnChanges(): void {
    this.stateChanges.next();
  }
  ngOnDestroy(): void {
    this._destroyed.next();
    this.stateChanges.complete();
  }

  ngOnInit(): void {
    this.setRequired();
    this.ngControl.statusChanges
      .pipe(
        takeUntil(this._destroyed),
        tap(() => this.setRequired())
      )
      .subscribe();
  }

  onBlur() {
    this.onTouched();
    this.stateChanges.next();
  }

  onChange = (_: any) => {
    // empty
  };

  onTouched = () => {
    // empty
  };

  writeValue(value: any): void {
    this.value = value;
    this.ref.markForCheck();
  }

  registerOnChange(fn: (_: any) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.ref.markForCheck();
  }

  onCountryChange(country: Country) {
    const value = country.iso2 as CountryISO;
    this.selectedCountry = value;
  }

  private setRequired() {
    const ctrl = this.ngControl.control;

    this.required = this.form.hasRequired(ctrl);
    this.stateChanges.next();
  }
}
